// In-memory index of schema objects with lookups by type, key and taxonomy; the same index serves server commands and the client store.

import type {
  AutomationDef, FieldDef, FormDef, RoleDef, Id, MetaObj, RelationDef, TaxonomyDef, TermDef, TypeDef, UserDef, ViewDef,
} from './model.ts';
import type { SettingDef } from './settings.ts';

const byOrder = (a: { order: number; id: Id }, b: { order: number; id: Id }) => a.order - b.order || (a.id < b.id ? -1 : 1);

export class MetaIndex {
  readonly all = new Map<Id, MetaObj>();
  private cache: {
    fields?: Map<Id, FieldDef[]>;
    terms?: Map<Id, TermDef[]>;
    children?: Map<Id, TermDef[]>;
    types?: TypeDef[];
    ranks?: Map<Id, Map<Id, number>>;
  } = {};

  constructor(objs: Iterable<MetaObj> = []) {
    for (const o of objs) this.all.set(o.id, o);
  }

  put(o: MetaObj): void { this.all.set(o.id, o); this.cache = {}; }
  delete(id: Id): void { this.all.delete(id); this.cache = {}; }

  get<T extends MetaObj>(id: Id | null | undefined, kind: T['kind']): T | undefined {
    const o = id ? this.all.get(id) : undefined;
    return o && o.kind === kind ? (o as T) : undefined;
  }

  type(id: Id | null | undefined) { return this.get<TypeDef>(id, 'type'); }
  taxonomy(id: Id | null | undefined) { return this.get<TaxonomyDef>(id, 'taxonomy'); }
  term(id: Id | null | undefined) { return this.get<TermDef>(id, 'term'); }
  relation(id: Id | null | undefined) { return this.get<RelationDef>(id, 'relation'); }
  user(id: Id | null | undefined) { return this.get<UserDef>(id, 'user'); }
  view(id: Id | null | undefined) { return this.get<ViewDef>(id, 'view'); }

  private list<T extends MetaObj>(kind: T['kind']): T[] {
    const out: T[] = [];
    for (const o of this.all.values()) if (o.kind === kind && !o.archivedAt) out.push(o as T);
    return out;
  }

  types(): TypeDef[] {
    return (this.cache.types ??= this.list<TypeDef>('type').sort(byOrder));
  }
  typeByKey(key: string) { return this.types().find((t) => t.key === key); }
  roles(): RoleDef[] { return this.list<RoleDef>('role').sort((a, b) => a.name.localeCompare(b.name, 'ru')); }
  role(id: Id | null | undefined) { return this.get<RoleDef>(id, 'role'); }
  users(): UserDef[] { return this.list<UserDef>('user').sort((a, b) => a.name.localeCompare(b.name, 'ru')); }
  relations(): RelationDef[] { return this.list<RelationDef>('relation'); }
  taxonomies(): TaxonomyDef[] { return this.list<TaxonomyDef>('taxonomy'); }
  forms(): FormDef[] { return this.list<FormDef>('form').sort((a, b) => a.name.localeCompare(b.name, 'ru')); }
  form(id: Id | null | undefined) { return this.get<FormDef>(id, 'form'); }
  automations(): AutomationDef[] { return this.list<AutomationDef>('automation').sort(byOrder); }
  automation(id: Id | null | undefined) { return this.get<AutomationDef>(id, 'automation'); }
  /** Live values of a setting (R-C13), every scope. */
  settings(key?: string): SettingDef[] { return this.list<SettingDef>('setting').filter((v) => !key || v.key === key); }
  /** Archived objects of a kind, for «Архив» lists and restore. */
  archived<T extends MetaObj>(kind: T['kind'], pick: (o: T) => boolean = () => true): T[] {
    const out: T[] = [];
    for (const o of this.all.values()) if (o.kind === kind && o.archivedAt && pick(o as T)) out.push(o as T);
    return out;
  }
  views(typeId: Id): ViewDef[] { return this.list<ViewDef>('view').filter((v) => v.typeId === typeId).sort(byOrder); }

  fields(typeId: Id): FieldDef[] {
    if (!this.cache.fields) {
      const m = new Map<Id, FieldDef[]>();
      for (const f of this.list<FieldDef>('field')) (m.get(f.typeId) ?? m.set(f.typeId, []).get(f.typeId)!).push(f);
      for (const l of m.values()) l.sort(byOrder);
      this.cache.fields = m;
    }
    return this.cache.fields.get(typeId) ?? [];
  }
  field(typeId: Id, key: string): FieldDef | undefined { return this.fields(typeId).find((f) => f.key === key); }

  terms(taxonomyId: Id): TermDef[] {
    if (!this.cache.terms) {
      const m = new Map<Id, TermDef[]>();
      for (const t of this.list<TermDef>('term')) (m.get(t.taxonomyId) ?? m.set(t.taxonomyId, []).get(t.taxonomyId)!).push(t);
      for (const l of m.values()) l.sort(byOrder);
      this.cache.terms = m;
    }
    return this.cache.terms.get(taxonomyId) ?? [];
  }

  children(parentId: Id | null, taxonomyId: Id): TermDef[] {
    return this.terms(taxonomyId).filter((t) => t.parentId === parentId);
  }

  subtree(termId: Id): Set<Id> {
    const root = this.term(termId);
    const out = new Set<Id>([termId]);
    if (!root) return out;
    const all = this.terms(root.taxonomyId);
    let added = true;
    while (added) {
      added = false;
      for (const t of all) if (t.parentId && out.has(t.parentId) && !out.has(t.id)) { out.add(t.id); added = true; }
    }
    return out;
  }

  /** Global position of a term inside its taxonomy for sorting (depth-first, by order); cached per taxonomy. */
  termRank(termId: Id): number {
    const t = this.term(termId);
    if (!t) return Number.MAX_SAFE_INTEGER;
    const ranks = (this.cache.ranks ??= new Map()).get(t.taxonomyId) ?? this.buildRanks(t.taxonomyId);
    return ranks.get(termId) ?? Number.MAX_SAFE_INTEGER;
  }

  private buildRanks(taxonomyId: Id): Map<Id, number> {
    const ranks = new Map<Id, number>();
    const walk = (parent: Id | null) => { for (const c of this.children(parent, taxonomyId)) { ranks.set(c.id, ranks.size); walk(c.id); } };
    walk(null);
    this.cache.ranks!.set(taxonomyId, ranks);
    return ranks;
  }

  /** The other end of a relation field: target type and whether the field holds one or many records. */
  relationTarget(field: FieldDef): { typeId: Id; many: boolean; relation: RelationDef } | undefined {
    const rel = this.relation(field.options.relationId);
    if (!rel) return undefined;
    const fromSide = field.options.side !== 'to';
    const typeId = fromSide ? rel.toTypeId : rel.fromTypeId;
    const many = rel.cardinality === 'many-to-many' || (rel.cardinality === 'many-to-one' && !fromSide);
    return { typeId, many, relation: rel };
  }
}
